// Chime synthesis with the Web Audio API so we don't need to ship a binary
// audio asset. Modern browsers suspend the AudioContext until a user gesture;
// playback is best-effort and swallows suspension errors. Shared by the
// customer "order ready" alert (useOrderReadyNotifier) and the store-side
// "new order" alert (useStoreOrderNotifier, #945).

// [frequency Hz, startOffset sec, duration sec]
export type ChimeTone = [number, number, number]

/**
 * Play a sequence of sine-wave tones. `peakGain` (0–1) controls loudness.
 * No-op (caught) when audio is unsupported or the context can't start.
 */
export function playChime(tones: ChimeTone[], peakGain = 0.25): void {
  try {
    type AudioCtor = typeof AudioContext
    const W = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor }
    const Ctor = W.AudioContext || W.webkitAudioContext
    if (!Ctor) return
    const ctx = new Ctor()
    // Best-effort resume in case the context starts suspended (autoplay policy).
    ctx.resume?.().catch(() => {})
    const now = ctx.currentTime
    let end = 0
    for (const [freq, start, dur] of tones) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, now + start)
      gain.gain.exponentialRampToValueAtTime(peakGain, now + start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur)
      osc.connect(gain).connect(ctx.destination)
      osc.start(now + start)
      osc.stop(now + start + dur + 0.02)
      end = Math.max(end, start + dur)
    }
    // Close after the chime finishes so we don't accumulate contexts.
    window.setTimeout(() => ctx.close().catch(() => {}), (end + 0.3) * 1000)
  } catch {
    // audio unsupported or suspended; callers fall back to visual cues
  }
}

// Two-tone descending pager chime — customer "your order is ready".
export const ORDER_READY_TONES: ChimeTone[] = [
  [880, 0, 0.18],
  [660, 0.2, 0.28],
]

// Three-tone rising chime — store kitchen "new order arrived" (#945).
// Distinct from ORDER_READY_TONES (rising vs falling) and from SERVED_TONES
// (arpeggio vs repeated single note, #1545), and played louder so staff in a
// noisy kitchen notice it.
export const NEW_ORDER_TONES: ChimeTone[] = [
  [660, 0, 0.14],
  [880, 0.15, 0.14],
  [1175, 0.3, 0.3],
]

// #962 (管理番号_009) 提供音 — 店員が注文を「提供済み」にした時に store 側で
// 鳴る確認音。
//
// #1545: 以前は 988 → 1319 Hz の**上昇**2音だった。注文音 (660→880→1175) と
// 向きが同じうえ音域も重なっており、**違いが「音の数」だけ**になっていた。
// 騒がしい店内で音の数は数えられないので、実際には聞き分けられていなかった。
//
// **同じ音の 2 連打**にする。人が無意識に聞き分けるのは音の上下の向きなので、
// 3 つの音がそれぞれ別の形になるようにした:
//
//   注文音     上昇 3 音   = 何か「来た」（注意を引く）
//   提供音     同音 2 連打 = 何か「終わった」（確認）        ← ここ
//   呼び出し音 下降 2 音   = できあがった（客側 + 受け取りカウンター）
//
// **下降にしない**のは、客側の ORDER_READY_TONES が既に下降 2 音で、
// StorePickupCounter では**店舗側でも鳴る**ため。連打なら両方と被らない。
export const SERVED_TONES: ChimeTone[] = [
  [587, 0, 0.09],
  [587, 0.14, 0.13],
]
